/* eslint-disable no-dupe-class-members */
import { BehaviorSubject, AsyncSubject, Observable, Subscription, PartialObserver, Subscribable } from 'rxjs';
import { map, switchMapTo, take } from 'rxjs/operators';

import { parseAuthToken, checkAuthorization } from './utils';
import doCheckCookie from './api/doCheckCookie';

export interface AuthTokenObject {
    TID: string;
    authorizations: string[];
    signature: string;
    timestamp: number;
    rawToken: string;
}

export type AuthTokenRawValue = string | null;
export type AuthTokenValue = AuthTokenObject | null;

export function isAuthTokenObject(tokenObj: AuthTokenValue): tokenObj is AuthTokenObject {
    return !!tokenObj && Array.isArray(tokenObj.authorizations);
}

class AuthToken implements Subscribable<AuthTokenValue> {
    private static _instance: AuthToken;
    public static readyState$ = new AsyncSubject<boolean>();
    public static value$ = new BehaviorSubject<AuthTokenRawValue>(null);

    public constructor() {
        if (AuthToken._instance) {
            return AuthToken._instance;
        }

        AuthToken._instance = this;

        // check login state and then set initial value
        doCheckCookie().then(
            tokenString => AuthToken.value$.next(typeof tokenString === 'string' && tokenString ? tokenString : null),
            () => AuthToken.value$.next(null)
        );

        // set ready state as soon as token value has been set
        AuthToken.readyState$.next(true);
        AuthToken.readyState$.complete();
    }

    /**
   * An interop point defined by the es7-observable spec https://github.com/zenparsing/es-observable
   * @method Symbol.observable
   * @return {Observable<AuthTokenValue>} this instance of the observable
   */
    public [Symbol.observable](): Observable<AuthTokenValue> {
        return this.asObservable();
    }

    /**
     * Returns an Observable parsed auth token object
     * @method asObservable
     * @param {string[]} [authorizations] An optional list of authorizations to require, observed value is null if value does not pass.
     * @returns {Observable<AuthTokenValue>}
     */
    public asObservable(authorizations?: string[]): Observable<AuthTokenValue> {
        return AuthToken.readyState$.pipe(
            switchMapTo(AuthToken.value$.asObservable()),
            map(parseAuthToken),
            map(tokenObj => (authorizations && !this.checkAuthorization(...authorizations)) ? null : tokenObj)
        );
    }

    /**
     * Checks if the current value of the auth token has the given authorizations
     * @method checkAuthorization
     * @param {...string[]} authorizations
     * @returns {boolean}
     */
    public checkAuthorization(...authorizations: string[]): boolean {
        return checkAuthorization(parseAuthToken(this.getValue()), ...authorizations);
    }

    /**
     * Get the current value of the auth token string
     * @method getValue
     * @returns {(string | null)}
     */
    public getValue(): string | null {
        return AuthToken.value$.value;
    }

    /**
     * Returns the first value only of the auth token as soon as its ready.
     * If a callback function is passed, returns the Subscription object
     */
    public onReady(): Observable<AuthTokenValue>;
    public onReady(onNext: (data: AuthTokenValue) => void): Subscription;
    public onReady(onNext?: (data: AuthTokenValue) => void): Observable<AuthTokenValue> | Subscription {
        const userData$ = this.asObservable().pipe(take(1));
        return typeof onNext === 'undefined' ? userData$ : userData$.subscribe(onNext);
    }

    /**
     * Sets the next value of the auth token string
     * @method setValue
     * @param {*} [value]
     * @returns
     */
    public setValue(value?: any) {
        return AuthToken.value$.next(typeof value === 'string' && value ? value : null);
    }

    public subscribe(): Subscription;
    public subscribe(observer: PartialObserver<AuthTokenValue>): Subscription;
    public subscribe(next?: (value: AuthTokenValue) => void, error?: (error: any) => void, complete?: () => void): Subscription;
    public subscribe(observerOrNext?: PartialObserver<AuthTokenValue> | ((value: AuthTokenValue) => void), error?: (error: any) => void, complete?: () => void): Subscription;
    /**
     * Subscribes to the parsed auth token object
     *
     * @method subscribe
     * @param {(PartialObserver<AuthTokenValue> | ((value: AuthTokenValue) => void))} [observerOrNext] Either an observer with methods to be called,
     *  or the first of three possible handlers, which is the handler for each value emitted from the subscribed
     *  Observable.
     * @param {(error: any) => void} [error] A handler for a terminal event resulting from an error. If no error handler is provided,
     *  the error will be thrown as unhandled.
     * @param {() => void} [complete] A handler for a terminal event resulting from successful completion.
     * @returns {Subscription}  a subscription reference to the registered handlers
     */
    public subscribe(
        observerOrNext?: PartialObserver<AuthTokenValue> | ((value: AuthTokenValue) => void),
        error?: (error: any) => void,
        complete?: () => void
    ): Subscription {
        if (typeof observerOrNext === 'function') {
            return this.asObservable().subscribe(observerOrNext, error, complete);
        } else {
            return this.asObservable().subscribe(observerOrNext);
        }
    }
}

export default new AuthToken();
